import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { fetchFinanceInvoices } from "../../../../../services/invoices";
import { filterInvoicesForFinanceView } from "../../../../../utils/invoiceDeposits";
import FinanceSectionShell from "./FinanceSectionShell";
import FinanceAssigneeAvatars from "./FinanceAssigneeAvatars";

const money = (value, currency = "CAD") => Number(value || 0).toLocaleString(undefined, { style: "currency", currency, minimumFractionDigits: 2 });
export const formatDepositCreatedAgo = (row = {}) => {
  const value = row.createdAt || row.invoiceDate;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? `Created ${formatDistanceToNow(date, { addSuffix: true })}` : "Created —";
};

export default function FinanceDeposits({ instanceUniqueId, limit = 8 }) {
  const [state, setState] = useState({ loading: true, error: "", rows: [] });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const invoices = await fetchFinanceInvoices({ instanceUniqueId, view: "invoices", page: 1, rpp: "all", sort: "invoiceDate", dir: "DESC" });
      setState({ loading: false, error: "", rows: filterInvoicesForFinanceView(invoices, "deposits") });
    } catch (_) {
      setState({ loading: false, error: "We couldn't load deposits.", rows: [] });
    }
  }, [instanceUniqueId]);
  useEffect(() => { load(); }, [load]);

  return (
    <FinanceSectionShell title="Deposits" subtitle="Recent deposit invoices and payment activity."
      countLabel={`${state.rows.length} recent`} loading={state.loading} error={state.error} onRetry={load}
      empty={!state.rows.length} emptyTitle="No deposits yet" emptyBody="Deposit invoices will appear here as they're created."
      viewAllHref={`/instance/${encodeURIComponent(instanceUniqueId)}/finance/invoicing?view=deposits`} viewAllLabel="View all deposits">
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
        {state.rows.slice(0, limit).map((row, index) => {
          const invoiceUrl = row.orderId && row.id ? `/instance/${encodeURIComponent(instanceUniqueId)}/sales/orders/${encodeURIComponent(row.orderId)}/invoice/${encodeURIComponent(row.id)}` : null;
          return <Box key={row.id || index} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr auto", md: "minmax(170px,1.3fr) minmax(120px,0.8fr) 110px 100px 150px 75px auto" }, gap: 1.25, alignItems: "center" }}>
            <Box><Typography fontWeight={750}>{row.customerName || "—"}</Typography><Typography variant="caption" color="text.secondary">Order {row.orderId || "—"}</Typography></Box>
            <Typography variant="body2" sx={{ display: { xs: "none", md: "block" } }}>Deposit #{row.id || "—"}</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ display: { xs: "none", md: "block" } }}>{money(row.amount, row.currency || "CAD")}</Typography>
            <Chip size="small" label={row.invoiceStatus || row.paymentStatus || "Open"} sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "start" }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>{formatDepositCreatedAgo(row)}</Typography>
            <FinanceAssigneeAvatars row={row} includeAdministrators />
            <Button component="a" href={invoiceUrl || undefined} target="_blank" rel="noopener noreferrer" disabled={!invoiceUrl} size="small" variant="outlined" sx={{ textTransform: "none" }}>View</Button>
          </Box>;
        })}
      </Stack>
    </FinanceSectionShell>
  );
}
